// Sign in (WEB_PLAN.md §4.1, §4.2, §5.1), the web twin of iOS SignInView in sign-in mode: Apple and Google first
// (each only when the server has it on and this build can run it), then email + password with "Forgot password?".
// A password sign-in to an account that never entered its emailed code (`email_not_confirmed`) sends a fresh code
// and continues on /verify, which saves the password again once the code confirms the account.
// After any sign-in AuthProvider reads `my_memberships` and the signed-out guard moves on to / or /welcome.

import { KeyRound, UsersRound } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useAuthOptions, eitherList } from '@/auth/authSettings';
import { authErrorMessage, isOptionTurnedOff } from '@/auth/errors';
import { isEmailLike, signInWithEmail } from '@/auth/flows';
import { peekSignedOutNotice, recalledEmail, rememberEmail, takeSignedOutNotice } from '@/auth/pending';
import { AuthCardBody, AuthHeader, AuthPage } from './components/AuthCard';
import { AuthModeSwitch } from './components/AuthModeSwitch';
import { ProviderButtons, type ProviderBusy } from './components/ProviderButtons';
import { ErrorText, Field, OrDivider, PasswordInput, PrimaryButton, PrivacyConsentLine, TextInput } from './components/ui';

type Busy = 'form' | Exclude<ProviderBusy, null> | null;

export default function SignInPage() {
  const navigate = useNavigate();
  const options = useAuthOptions();
  // "Account deleted", "Your session expired…": shown once, on the first sign-in screen after it happened. Read
  // during render, consumed after mount (render may run twice in StrictMode).
  const [notice] = useState(() => peekSignedOutNotice());
  useEffect(() => {
    takeSignedOutNotice();
  }, []);
  const [email, setEmail] = useState(() => recalledEmail());
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState<Busy>(null);
  const [error, setError] = useState<string | null>(null);
  const [providerError, setProviderError] = useState<string | null>(null);

  const showsProviders = options.apple || options.google;
  const canSignIn = isEmailLike(email) && password !== '' && busy === null;

  // "Use Apple, Google, or the email address you signed up with.", naming only what's offered (iOS signInSubtitle).
  const ways: string[] = [];
  if (options.apple) ways.push('Apple');
  if (options.google) ways.push('Google');
  if (options.email) ways.push('the email address you signed up with');
  const subtitle = `Use ${eitherList(ways)}.`;

  const submit = async () => {
    if (!canSignIn) return;
    setBusy('form');
    setError(null);
    setProviderError(null);
    try {
      const result = await signInWithEmail(email, password);
      setPassword('');
      if (result === 'needsVerification') navigate('/verify');
      // Signed in: AuthProvider takes over and this page goes away; stay busy meanwhile.
    } catch (e) {
      setError(authErrorMessage(e));
      if (isOptionTurnedOff(e)) options.refresh();
      setBusy(null);
    }
  };

  return (
    <AuthPage>
      <AuthHeader icon={<UsersRound className="h-6 w-6" aria-hidden />} title="Sign in to Ground Team" subtitle={subtitle} />
      {notice && (
        <div role="status" className="rounded-2xl border border-[var(--gt-green)]/30 bg-[var(--gt-green)]/10 px-4 py-3 text-sm">
          <p className="font-semibold text-slate-900 dark:text-slate-100">{notice.title}</p>
          <p className="text-slate-700 dark:text-slate-300">{notice.message}</p>
        </div>
      )}
      <AuthModeSwitch mode="signIn" disabled={busy !== null} />
      <AuthCardBody as="form" onSubmit={() => void submit()}>
        <ProviderButtons
          mode="signIn"
          apple={options.apple}
          google={options.google}
          busy={busy === 'form' ? null : busy}
          disabled={busy === 'form'}
          onBusyChange={(b) => setBusy(b)}
          onError={(message) => {
            setError(null);
            setProviderError(message);
          }}
          onOptionTurnedOff={options.refresh}
        />
        <ErrorText>{providerError}</ErrorText>
        {options.email && (
          <>
            {showsProviders && <OrDivider />}
            <Field label="Email">
              {(id) => (
                <TextInput
                  id={id}
                  type="email"
                  inputMode="email"
                  autoComplete="username"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                  placeholder="name@company.com"
                  aria-label="Email address"
                  value={email}
                  disabled={busy !== null}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    rememberEmail(e.target.value);
                  }}
                />
              )}
            </Field>
            <Field
              label="Password"
              trailing={
                <Link
                  to="/forgot"
                  onClick={() => rememberEmail(email)}
                  className="text-xs font-semibold text-[var(--gt-orange)] hover:underline"
                >
                  Forgot password?
                </Link>
              }
            >
              {(id) => (
                <PasswordInput
                  id={id}
                  revealed={false}
                  showsRevealButton={false}
                  autoComplete="current-password"
                  placeholder="Password"
                  value={password}
                  disabled={busy !== null}
                  onChange={(e) => setPassword(e.target.value)}
                />
              )}
            </Field>
            <ErrorText>{error}</ErrorText>
            <PrimaryButton
              type="submit"
              busy={busy === 'form'}
              busyLabel="Signing in…"
              icon={<KeyRound className="h-4 w-4" aria-hidden />}
              disabled={!canSignIn}
            >
              Sign in
            </PrimaryButton>
          </>
        )}
      </AuthCardBody>
      <PrivacyConsentLine />
    </AuthPage>
  );
}
